package diana.soleil.design.iraniancanadienbazzar.core.common

import kotlin.math.roundToInt

/**
 * Pure, locale-agnostic value formatters (no user-facing copy — those stay in string resources).
 * These shape raw domain values (ratings, phone numbers) for display.
 */
object Formatters {

    /** Rating to a single decimal, e.g. `4.8f` -> `"4.8"`, `5.0f` -> `"5.0"`. */
    fun rating(value: Float): String {
        // Round to nearest: floats like 4.6 are stored as 4.5999999…, and truncation would
        // display them a full tenth low ("4.5"). Ratings are non-negative.
        val rounded = (value * 10).roundToInt()
        return "${rounded / 10}.${rounded % 10}"
    }

    /**
     * Pretty-prints a North-American E.164 number for display, e.g. `"+14165550156"` ->
     * `"(416) 555-0156"`. Returns the input unchanged if it is not a 10-digit NANP number.
     */
    fun phoneNanp(e164: String): String {
        val digits = e164.filter { it.isDigit() }
        val national = when {
            digits.length == 11 && digits.startsWith("1") -> digits.substring(1)
            digits.length == 10 -> digits
            else -> return e164
        }
        val area = national.substring(0, 3)
        val prefix = national.substring(3, 6)
        val line = national.substring(6)
        return "($area) $prefix-$line"
    }

    /**
     * Normalizes Farsi (Persian) text by mapping Arabic character variants (Ye/Ke) to their
     * canonical Persian forms. Essential for reliable search across different input sources.
     *
     * Variants mapped:
     * - \u064a (Arabic Ye 'ي') -> \u06cc (Persian Ye 'ی')
     * - \u0643 (Arabic Ke 'ك') -> \u06a9 (Persian Ke 'ک')
     * - \u0649 (Alef Maksura 'ى') -> \u06cc (Persian Ye 'ی')
     */
    fun normalizePersian(text: String): String {
        return text
            .replace('\u064a', '\u06cc') // Arabic Ye
            .replace('\u0643', '\u06a9') // Arabic Ke
            .replace('\u0649', '\u06cc') // Alef Maksura
    }

    /**
     * Search-grade Persian folding, applied to BOTH the query and the indexed text: the letter
     * variants above, plus the zero-width non-joiner (typed inconsistently — "دندان‌پزشک" and
     * "دندانپزشک" must match), tatweel, Arabic diacritics, hamza-on-alef forms, and the
     * Persian/Arabic digit blocks folded to ASCII. Without this a Farsi query only matched text
     * typed with exactly the same invisible characters.
     */
    fun foldForSearch(text: String): String {
        val sb = StringBuilder(text.length)
        // Lowercase first so the Latin accent table below needs one case only.
        for (ch in normalizePersian(text).lowercase()) {
            when (ch) {
                '\u200c', '\u200d', '\u0640' -> Unit                   // ZWNJ, ZWJ, tatweel
                in '\u064b'..'\u065f', '\u0670' -> Unit                // Arabic diacritics
                '\u0622', '\u0623', '\u0625' -> sb.append('\u0627')     // آ أ إ → ا
                '\u0624' -> sb.append('\u0648')                         // ؤ → و
                '\u0626' -> sb.append('\u06cc')                         // ئ → ی
                '\u0629' -> sb.append('\u0647')                         // ة → ه
                // Latin accents: "Château Kabab" must be found by someone typing "Chateau"
                // (QA 2026-09-12). French listings carry é/è/ç, transliterated Persian names â/î/û.
                'à', 'á', 'â', 'ã', 'ä', 'å' -> sb.append('a')
                'ç' -> sb.append('c')
                'è', 'é', 'ê', 'ë' -> sb.append('e')
                'ì', 'í', 'î', 'ï' -> sb.append('i')
                'ñ' -> sb.append('n')
                'ò', 'ó', 'ô', 'õ', 'ö' -> sb.append('o')
                'ù', 'ú', 'û', 'ü' -> sb.append('u')
                'ý', 'ÿ' -> sb.append('y')
                'æ' -> sb.append("ae")
                'œ' -> sb.append("oe")
                'ß' -> sb.append("ss")
                in '\u06f0'..'\u06f9' -> sb.append('0' + (ch - '\u06f0'))  // Persian digits
                in '\u0660'..'\u0669' -> sb.append('0' + (ch - '\u0660'))  // Arabic digits
                else -> sb.append(ch)
            }
        }
        return sb.toString()
    }
}
